import { Redirect, Route, Switch } from 'react-router';

import { HeaderWithNavigation } from 'src/core/common/components/HeaderWithNavigation/HeaderWithNavigation';
import type { NavigationLinkProps } from 'src/core/common/components/NavigationLink';
import { ProtectedRoute } from 'src/core/common/components/ProtectedRoute';
import { useFeature } from 'src/core/common/hooks/useFeature';
import {
  type I18nKey,
  useTranslation,
} from 'src/core/common/hooks/useTranslation';
import FEATURES from 'src/core/constants/features';
import { routeFor, routes } from 'src/core/constants/routes';
import { useHasMapSync } from 'src/core/modules/accounting-integration/hooks/useHasMapSync';
import { useCompany } from 'src/core/modules/app/hooks/useCompany';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';
import { useUser } from 'src/core/modules/app/hooks/useUser';
import { useIntegrationStatusQuery } from 'src/core/modules/bookkeep';
import type {
  AccountingSoftware,
  AnalyticalFieldsCapability,
} from 'src/core/modules/bookkeep/integration/status';
import ExportsContainer from 'src/core/modules/bookkeep/settings/export/ExportsContainer';
import { canAccessAnalyticalFieldsSettings } from 'src/core/modules/bookkeep/settings/integrations/helper/settings-access';
import { IntegrationsAccountingSetupPage } from 'src/core/modules/bookkeep/settings/integrations/pages/IntegrationsAccountingSetupPage/IntegrationsAccountingSetupPage';
import { AnalyticalFieldsSection } from 'src/core/modules/bookkeep/settings/integrations/pages/LegacyIntegrationsAccountingPage/sections/AnalyticalFieldsSection';
import { BookkeepingStartDateSectionContainer } from 'src/core/modules/bookkeep/settings/integrations/pages/LegacyIntegrationsAccountingPage/sections/BookkeepingSetupSection';
import { SpanishDprSection } from 'src/core/modules/bookkeep/settings/integrations/pages/LegacyIntegrationsAccountingPage/sections/SpanishDprSection/SpanishDprSection';
import { PayableRulesSetupPageContainer } from 'src/core/modules/bookkeep/settings/integrations/pages/PayableRulesSetupPage/PayableRulesSetupPageContainer';
import { rejectUnexpectedValue } from 'src/core/utils/switchGuard';

function getAnalyticalFieldsI18nKey(
  integration: AccountingSoftware,
  isMapSyncEnabled: boolean,
): I18nKey {
  if (isMapSyncEnabled) {
    return 'bookkeep.integrations.settings.analyticalFieldsMappingsTable.mapSyncTable.routeHeader';
  }

  switch (integration) {
    case 'Datev':
      return 'bookkeep.integrations.datev.analyticalFieldsHeader';
    case 'Quickbooks':
    case 'Xero':
    case 'SpendeskAccounting':
    case 'SpendeskAccountingSingleEntry':
    case 'Netsuite':
    case 'Sage':
    case 'Cegid':
    case 'Sage100':
    case 'ACD':
    case 'Odoo':
    case 'SageIntacct':
    case 'MicrosoftDynamics':
    case 'ExactOnlineFr':
    case 'ExactOnlineBe':
    case 'ExactOnlineNl':
    case 'DatevRewe':
    case 'PublicApi':
      return 'bookkeep.integrations.settings.sectionAnalyticalFields';
    default:
      rejectUnexpectedValue('integration type', integration);
  }
}

export const SettingsAccountingPage = () => {
  const { t } = useTranslation('global');
  const companyId = useCompanyId();
  const company = useCompany();
  const user = useUser();
  const isAccountingExportsSettingsEnabled = useFeature(
    FEATURES.SETTINGS_ACCOUNTING_EXPORTS,
  );
  const isAccountingSettingsEnabled = useFeature(FEATURES.SETTINGS_ACCOUNTING);
  const isSpanishDprSettingsEnabled = useFeature(
    FEATURES.TMP_SPANISH_DPR_PHASE_2,
  );

  const isAdvancedRuleBasedAutomationEnabled = useFeature(
    FEATURES.ADVANCED_RULE_BASED_AUTOMATION,
  );
  const isMapSyncEnabled = useHasMapSync();

  const accountingIntegrationStatusQueryResult = useIntegrationStatusQuery();
  const integrationsStatus =
    accountingIntegrationStatusQueryResult.status === 'success'
      ? accountingIntegrationStatusQueryResult.data
      : undefined;

  const hasIntegration =
    accountingIntegrationStatusQueryResult.status === 'success' &&
    accountingIntegrationStatusQueryResult.data.integration !== 'noIntegration';

  const links: NavigationLinkProps[] = [];

  const isAoOrController = user.is_account_owner || user.is_controller;
  const isAoOrAdminOrController = isAoOrController || user.is_admin;

  if (isAccountingSettingsEnabled && isAoOrController) {
    links.push({
      key: 'accounting-integration',
      path: routeFor(routes.COMPANY_ACCOUNTING_INTEGRATION.path, {
        company: companyId,
      }),
      text: t('bookkeep.integrations.settings.sectionAccountingSoftware'),
    });
  }

  if (canAccessAnalyticalFieldsSettings(integrationsStatus)) {
    links.push({
      key: 'custom-fields',
      text: t(
        getAnalyticalFieldsI18nKey(
          integrationsStatus.integration,
          isMapSyncEnabled,
        ),
      ),
      path: routeFor(routes.COMPANY_ACCOUNTING_ANALYTICAL_FIELDS.path, {
        company: companyId,
      }),
    });
  }

  if (isAccountingSettingsEnabled && isAoOrController) {
    links.push({
      key: 'bookkeeping-date',
      path: routeFor(routes.COMPANY_ACCOUNTING_BOOKKEEPING_SETUP.path, {
        company: companyId,
      }),
      text: t('bookkeep.integrations.settings.sectionBookkeepingSetup'),
    });
  }

  if (isAdvancedRuleBasedAutomationEnabled && isAoOrAdminOrController) {
    links.push({
      key: 'rule-based-automation', // aka payable rules
      path: routeFor(routes.COMPANY_ACCOUNTING_PAYABLE_RULES_SETUP.path, {
        company: companyId,
      }),
      text: t('bookkeep.integrations.settings.sectionRuleBasedAutomation'),
    });
  }

  if (
    isAccountingExportsSettingsEnabled &&
    hasIntegration &&
    !company.churning_at &&
    isAoOrController
  ) {
    links.push({
      key: 'exports',
      path: routeFor(routes.COMPANY_EXPORTS.path, {
        company: companyId,
      }),
      text: t('settings.links.exports'),
    });
  }

  if (isSpanishDprSettingsEnabled && isAoOrAdminOrController) {
    links.push({
      key: 'spanish-dpr',
      path: routeFor(routes.COMPANY_SPANISH_DPR.path, {
        company: companyId,
      }),
      text: t('settings.links.spanishDpr'),
    });
  }

  return (
    <div className="page__container">
      <HeaderWithNavigation links={links}>
        {t('navigation.accounting')}
      </HeaderWithNavigation>
      <Switch>
        <Route exact path={routes.COMPANY_ACCOUNTING_INTEGRATION.path}>
          <div className="bg-background-primary-default flex w-full max-w-lg flex-col self-center px-40 py-24">
            {isAoOrAdminOrController && <IntegrationsAccountingSetupPage />}
          </div>
        </Route>
        <Route path={routes.COMPANY_ACCOUNTING_BOOKKEEPING_SETUP.path}>
          <div className="bg-background-primary-default flex w-full max-w-lg flex-col self-center px-40 py-24">
            <BookkeepingStartDateSectionContainer />
          </div>
        </Route>
        <Route path={routes.COMPANY_ACCOUNTING_PAYABLE_RULES_SETUP.path}>
          <div className="bg-background-primary-default flex w-full flex-col self-center px-40 py-24">
            <PayableRulesSetupPageContainer />
          </div>
        </Route>
        <Route path={routes.COMPANY_EXPORTS.path}>
          <div className="bg-background-primary-default flex w-full max-w-lg flex-col self-center px-40 py-24">
            <ExportsContainer />
          </div>
        </Route>
        <ProtectedRoute
          path={routes.COMPANY_ACCOUNTING_ANALYTICAL_FIELDS.path}
          isAccessAllowed={canAccessAnalyticalFieldsSettings(
            integrationsStatus,
          )}
        >
          {canAccessAnalyticalFieldsSettings(integrationsStatus) && (
            <div className="page__container">
              <div className="flex w-full flex-col p-40">
                <AnalyticalFieldsSection
                  integration={integrationsStatus.integration}
                  analyticalFieldsCapability={
                    integrationsStatus.capabilities
                      .analyticalFields as AnalyticalFieldsCapability
                  }
                />
              </div>
            </div>
          )}
        </ProtectedRoute>
        <Route path={routes.COMPANY_SPANISH_DPR.path}>
          {isSpanishDprSettingsEnabled && isAoOrAdminOrController && (
            <div className="bg-background-primary-default flex w-full max-w-lg flex-col self-center px-32 py-24">
              <SpanishDprSection />
            </div>
          )}
        </Route>
        <Redirect
          from={routes.COMPANY_ACCOUNTING.path}
          to={routes.COMPANY_ACCOUNTING_INTEGRATION.path}
        />
      </Switch>
    </div>
  );
};
